/**
 * History › Champions and payouts — built to turn `3b`.
 *
 * Four summary tiles over a table of one row per season: champion, runner-up, toilet bowl, the
 * regular-season points leader, and what the season paid. The drawing sets the champion in ink
 * and everybody else muted, so the row's focus is who won it.
 *
 * Two honesty rules the data forces, both stated on the screen rather than hidden:
 *
 *   - **Payouts start in 2019.** That is when the $90 buy-in and this split began. Before it the
 *     rulebook records that dues existed (2015-I-3) without ever naming an amount, so those
 *     seasons show a dash. A payout column that invented pre-2019 numbers would be quoted back
 *     at somebody in an argument.
 *   - **A past champion's figure is a floor, except in 2019.** Real money reached the pot through
 *     FAAB purchases (2020-I-6), never logged, so those seasons read `$710+`. 2019 is the one
 *     season with a ceiling: the only way to add money that year was cash for a 6th or 7th keeper
 *     (2019-III-3), and the keeper archive says how many GMs could have paid — so it reads as a
 *     range instead.
 *
 * The toilet bowl is a separate bracket, not last place, so it is derived from the consolation
 * games themselves — see `toiletBowlWinner` in the domain. That covers 2010 to 2025; earlier
 * seasons have no bracket in the archive and 2015's final was a tie, and both show a dash.
 */

import { useMemo } from 'react';
import standings from '../data/standings-history.json';
import { gmFor, managerShort } from '../domain/identity.js';
import {
  PAYOUTS, championsByYear, droughts, managerMoney, payoutsFor, regularSeasonHighScore,
  toiletBowlWinner,
} from '../domain/payouts.js';
import { usePairingVersion } from './pairings.js';
import { GMS } from '../store/session.js';
import { GmBadge } from './GmBadge.js';
import { Citations, Linkify } from './RuleText.js';

const SEASONS = (standings as unknown as { seasons: Array<{ season: number; standings: Array<{ rank: number; gm: string | null; team: string }> }> }).seasons;

const money = (n: number) => `$${n.toLocaleString('en-US')}`;

export function HistoryChampions({ gm }: { gm: string }) {
  /*
   * TWO BUGS LIVED HERE, and they hid each other.
   *
   * This screen read the archive's own `gm` field and printed the TEAM NAME whenever it was null —
   * which is every pre-2009 season, because back then the export only carried team names. Settings
   * › Manager history exists precisely to attach a person to those, and this screen was the one
   * place that never asked. `gmFor` is the resolver every other records screen already goes
   * through; it returns a named gm unchanged and consults the pairings otherwise.
   *
   * And the memo was keyed on `[]`, so even once the pairing was saved this table did not
   * recompute. `usePairingVersion()` was already being called — its value was simply thrown away.
   */
  const pairings = usePairingVersion();

  const rows = useMemo(() => championsByYear().map((c) => {
    const table = SEASONS.find((s) => s.season === c.season);
    const runnerUp = table?.standings.find((r) => r.rank === 2);
    const toilet = toiletBowlWinner(c.season);
    const pay = payoutsFor(c.season);
    return {
      season: c.season,
      champion: gmFor(c.season, { gm: c.gm, team: c.team }),
      championTeam: c.team,
      runnerUp: gmFor(c.season, { gm: runnerUp?.gm, team: runnerUp?.team }),
      runnerUpTeam: runnerUp?.team ?? '',
      // Derived from the consolation bracket, not from who finished last — see the domain.
      // `ToiletBowl` carries only a gm, which for an unpaired season is holding a team name —
      // exactly the case `gmFor` handles when `team` is absent.
      toiletBowl: toilet ? gmFor(c.season, { gm: toilet.gm }) : null,
      high: regularSeasonHighScore(c.season),
      payout: pay?.lines[0] ?? null,
    };
  }), [pairings]);

  const mine = useMemo(() => managerMoney(gm), [gm]);

  const mostTitles = useMemo(() => {
    const count = new Map<string, number>();
    for (const r of rows) if (r.champion) count.set(r.champion, (count.get(r.champion) ?? 0) + 1);
    const best = [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
    return best ? { gm: best[0], titles: best[1] } : null;
  }, [rows]);

  const biggest = useMemo(() => rows
    .filter((r) => r.payout)
    .sort((a, b) => (b.payout!.upTo ?? b.payout!.amount) - (a.payout!.upTo ?? a.payout!.amount))[0]
    ?? null, [rows]);

  const drought = useMemo(() => droughts(GMS)[0] ?? null, []);

  return (
    <div className="app">
      <Linkify>
        <header className="pagehead">
          <div>
            <h1>Champions and payouts</h1>
            <p className="sub">
              {rows.length} seasons, {rows[rows.length - 1]?.season} to {rows[0]?.season} ·
              {' '}payouts from {PAYOUTS.from}
            </p>
          </div>
        </header>

        <div className="champtiles">
          <section className="card tilecard">
            <span className="tilelabel">Most titles</span>
            <b className="tileval">
              {mostTitles ? `${managerShort(mostTitles.gm)} ×${mostTitles.titles}` : '—'}
            </b>
            <span className="tilenote">of {rows.length} seasons</span>
          </section>
          <section className="card tilecard">
            <span className="tilelabel">Largest payout</span>
            <b className="tileval">
              {biggest
                ? `${money(biggest.payout!.amount)}${biggest.payout!.upTo ? `–${money(biggest.payout!.upTo)}` : biggest.payout!.atLeast ? '+' : ''}`
                : '—'}
            </b>
            <span className="tilenote">{biggest ? biggest.season : `nothing before ${PAYOUTS.from}`}</span>
          </section>
          <section className="card tilecard">
            <span className="tilelabel">Longest title drought</span>
            <b className="tileval">
              {drought ? managerShort(drought.gm) : '—'}
            </b>
            <span className="tilenote">
              {drought
                ? drought.everWon ? `${drought.seasons} seasons` : `never, ${drought.seasons} seasons in`
                : ''}
            </span>
          </section>
          <section className="card tilecard">
            <span className="tilelabel">Your record</span>
            <b className="tileval">{mine.titles} title{mine.titles === 1 ? '' : 's'}</b>
            <span className="tilenote">
              {mine.finals} final{mine.finals === 1 ? '' : 's'} · net{' '}
              <span className={mine.net >= 0 ? 'ok' : 'bad'}>
                {mine.net >= 0 ? '+' : '−'}{money(Math.abs(mine.net))}
              </span>
            </span>
          </section>
        </div>

        <section className="card standingscard">
          <table className="standings champs">
            <thead>
              <tr>
                <th className="rk">Season</th>
                <th>Champion</th>
                <th>Runner-up</th>
                <th>Toilet bowl</th>
                <th>Top scorer</th>
                <th className="num">Payout</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.season}>
                  <td className="rk yr">{r.season}</td>
                  <td className="champ">
                    {r.champion
                      ? <><GmBadge gm={r.champion} /> <b>{managerShort(r.champion)}</b></>
                      : <span className="unpairedmark">{r.championTeam || 'not paired'}</span>}
                  </td>
                  <td className="second">
                    {r.runnerUp ? managerShort(r.runnerUp)
                      : <span className="unpairedmark">{r.runnerUpTeam || '—'}</span>}
                  </td>
                  <td className="toilet">
                    {r.toiletBowl ? managerShort(r.toiletBowl) : <span className="none">—</span>}
                  </td>
                  <td className="scorer">
                    {r.high
                      ? <>{managerShort(r.high.gm)} <span className="pts">{r.high.points.toLocaleString('en-US')}</span></>
                      : <span className="none">—</span>}
                  </td>
                  <td className="num pay">
                    {r.payout
                      ? (
                        <b title={r.payout.upTo
                          ? `Between ${money(r.payout.amount)} and ${money(r.payout.upTo)}, depending on how many GMs paid cash rather than FAAB for a sixth or seventh keeper`
                          : undefined}>
                          {money(r.payout.amount)}
                          {r.payout.upTo
                            ? `–${money(r.payout.upTo)}`
                            : r.payout.atLeast ? '+' : ''}
                        </b>
                      )
                      : <span className="none">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="railfoot">
            Since {PAYOUTS.from}: {money(PAYOUTS.buyIn)} a team, {money(PAYOUTS.highScore)} to the
            regular-season high score, {money(PAYOUTS.runnerUp)} to second, the rest to the
            champion. A <b>+</b> means real money also reached that pot through FAAB purchases,
            which were never logged, so the champion won at least that much. {PAYOUTS.from} is
            the one season with a ceiling: nine GMs kept six or more and four kept seven, and
            each could have paid $10 and $20 in cash rather than FAAB, so the pot was between
            $900 and $1,070. The toilet bowl is the consolation bracket&rsquo;s winner, worked
            out from the scores — 2009 and earlier have no bracket in the archive, and
            2015&rsquo;s final was a tie.{' '}
            <Citations ids={['2020-I-6', '2019-III-3', '2020-III-1', '2015-I-3']} />
          </p>
        </section>
      </Linkify>
    </div>
  );
}
